import { Component, Property } from '@wonderlandengine/api';
// Simple input bridge that forwards native mouse/keyboard to VoxelTreadmill
export class PlayerInput extends Component {
    static TypeName = 'player-input';
    static Properties = {
        treadmillObject: Property.object(), // assign VoxelTreadmill object in editor
        pistolObject: Property.object(), // assign FpsPistol owner object or component
        miningToolObject: Property.object(), // assign MiningTool owner object
    };

    start() {
        this._keys = {};
        this._mouseDown = false;
        this._mouseButton = -1;
        this._movementBuffer = { movementX: 0, movementY: 0 };
        this._prevMouseDown = false;
        this._prevMouseButton = -1;

        this._isWheelOpen = false;
        this._isCraftingOpen = false;
        this._activeId = 'mining';

        this._canvas = (this.engine && this.engine.canvas) || document.querySelector('canvas') || document.body;

        this._boundMouseMove = (e) => {
            if (this._isWheelOpen) return;
            this._movementBuffer.movementX += e.movementX || 0;
            this._movementBuffer.movementY += e.movementY || 0;
        };
        this._boundMouseDown = (e) => {
            if (this._isWheelOpen) {
                // Close wheel if clicking background (not on an item)
                if (!e.target.closest('.wheel-item')) this.toggleWheel(false);
                return;
            }
            this._mouseDown = true; this._mouseButton = e.button;

            if (this._treadmill?.pointerLockOnClick && !document.pointerLockElement) {
                try { this._canvas.requestPointerLock(); } catch (_) { }
            }
        };
        this._boundMouseUp = (e) => {
            this._mouseDown = false;
            this._mouseButton = -1;
        };
        this._boundWheel = (e) => {
            if (!this._treadmill) return;
            let id = this._treadmill.buildMaterialId + (e.deltaY > 0 ? 1 : -1);
            if (id < 1) id = 5; if (id > 5) id = 1;
            this._treadmill.buildMaterialId = id;
            if (window.GameInterface) window.GameInterface.updateActiveBlueprint(id);
        };
        this._boundKeyDown = (e) => {
            const key = e.key.toLowerCase();
            this._keys[key] = true;
            if (key === 'tab') {
                e.preventDefault();
                this.toggleWheel();
            }
            if (key === 'c' && window.GameState === 'playing') {
                e.preventDefault();
                this.toggleCrafting();
            }
            if (key === 'q' && window.GameState === 'playing') {
                window.GameInterface?.useShieldCell?.();
            }
            if (key === 'e' && window.GameState === 'playing') {
                window.GameInterface?.useMedkit?.();
            }
            if (key >= '1' && key <= '5' && this._treadmill) {
                const id = parseInt(key);
                this._treadmill.buildMaterialId = id;
                if (window.GameInterface) window.GameInterface.updateActiveBlueprint(id);
            }
        };
        this._boundKeyUp = (e) => { this._keys[e.key.toLowerCase()] = false; };

        // Resolve treadmill component. If treadmillObject provided, prefer that.
        if (this.treadmillObject) this._treadmill = this.treadmillObject.getComponent('voxel-treadmill');
        else this._treadmill = (window.VoxelTreadmill || null);

        if (this._treadmill && this._treadmill.useInputBridge === false) {
            try { this._treadmill.useInputBridge = true; } catch (_) { }
        }

        // Setup the UI API Bridge
        window.GameInterface.toggleCraftingFromEngine = () => this.toggleCrafting();

        window.GameInterface.selectWeapon = (id) => {
            this._activeId = id;
            this._applySelection();
            this.toggleWheel(false);
        };

        this._pistol = this.pistolObject ? this.pistolObject.getComponent('fps-pistol') : null;
        this._miningTool = this.miningToolObject ? this.miningToolObject.getComponent('mining-tool') : null;
        this._applySelection();
    }

    onActivate() {
        window.addEventListener('mousemove', this._boundMouseMove);
        window.addEventListener('mousedown', this._boundMouseDown);
        window.addEventListener('mouseup', this._boundMouseUp);
        window.addEventListener('wheel', this._boundWheel);
        window.addEventListener('keydown', this._boundKeyDown);
        window.addEventListener('keyup', this._boundKeyUp);
    }

    onDeactivate() {
        window.removeEventListener('mousemove', this._boundMouseMove);
        window.removeEventListener('mousedown', this._boundMouseDown);
        window.removeEventListener('mouseup', this._boundMouseUp);
        window.removeEventListener('wheel', this._boundWheel);
        window.removeEventListener('keydown', this._boundKeyDown);
        window.removeEventListener('keyup', this._boundKeyUp);

        // Reset state so movement/firing doesn't get stuck
        this._keys = {};
        this._mouseDown = false;
        this._prevMouseDown = false;
        this._prevMouseButton = -1;
        if (this._treadmill) {
            if (!this._isCraftingOpen && !this._isWheelOpen) {
                this._treadmill.enablePointerLook = true;
            }
            this._treadmill.setKeys({});
        }
    }

    toggleWheel(force) {
        this._isWheelOpen = force !== undefined ? force : !this._isWheelOpen;
        // Mutually exclusive with crafting panel
        if (this._isWheelOpen && this._isCraftingOpen) this.toggleCrafting(false);

        if (window.GameInterface.toggleWeaponWheel) {
            window.GameInterface.toggleWeaponWheel(this._isWheelOpen);
        }

        if (this._treadmill) {
            this._treadmill.enablePointerLook = !this._isWheelOpen && !this._isCraftingOpen;
            if (this._isWheelOpen) {
                this._treadmill.setKeys({});
                this._treadmill.setMouseState({ movementX: 0, movementY: 0 });
            }
        }

        if (this._isWheelOpen) {
            if (document.pointerLockElement) document.exitPointerLock();
            this._mouseDown = false;
            this._prevMouseDown = false;
            this._prevMouseButton = -1;
        }
    }

    toggleCrafting(force) {
        this._isCraftingOpen = force !== undefined ? force : !this._isCraftingOpen;
        // Mutually exclusive with weapon wheel
        if (this._isCraftingOpen && this._isWheelOpen) this.toggleWheel(false);

        if (window.GameInterface.toggleCraftingPanel) {
            window.GameInterface.toggleCraftingPanel(this._isCraftingOpen);
        }

        if (this._treadmill) {
            this._treadmill.enablePointerLook = !this._isCraftingOpen && !this._isWheelOpen;
            if (this._isCraftingOpen) {
                this._treadmill.setKeys({});
                this._treadmill.setMouseState({ movementX: 0, movementY: 0 });
            }
        }

        if (this._isCraftingOpen) {
            if (document.pointerLockElement) document.exitPointerLock();
            this._mouseDown = false;
            this._prevMouseDown = false;
            this._prevMouseButton = -1;
        }
    }

    _applySelection() {
        // VRControls owns miningToolObject / pistolObject during an XR session.
        // Running here would fight its active-state management and can trigger
        // MiningTool.onDeactivate → stopMining(), wiping laser state mid-frame.
        if (this.engine?.xr?.session) return;

        const isMining = this._activeId === 'mining';
        const isPistol = this._activeId === 'pistol';

        [
            { obj: this.miningToolObject, active: isMining },
            { obj: this.pistolObject, active: isPistol }
        ].forEach(slot => {
            if (slot.obj) {
                slot.obj.active = slot.active;
                // Toggle components to trigger onActivate/onDeactivate
                slot.obj.getComponents().forEach(c => c.active = slot.active);
            }
        });

        if (window.GameInterface.setSelectedWeapon) {
            window.GameInterface.setSelectedWeapon(this._activeId);
        }
    }

    update(dt) {
        if (window.GameState !== 'playing') return;
        if (this.engine?.xr?.session) return;
        if (this._isWheelOpen || this._isCraftingOpen) return;

        // Forward keys to treadmill bridge (w/a/s/d/shift/space)
        if (this._treadmill && typeof this._treadmill.setKeys === 'function') {
            const mapped = {
                w: !!this._keys['w'],
                a: !!this._keys['a'],
                s: !!this._keys['s'],
                d: !!this._keys['d'],
                shift: !!this._keys['shift'],
                ' ': !!this._keys[' '],
                arrowup: !!this._keys['arrowup'],
                arrowdown: !!this._keys['arrowdown'],
                arrowleft: !!this._keys['arrowleft'],
                arrowright: !!this._keys['arrowright']
            };
            this._treadmill.setKeys(mapped);

            // Mouse look / pointer
            this._treadmill.setMouseState({ down: this._mouseDown, button: this._mouseButton, movementX: this._movementBuffer.movementX, movementY: this._movementBuffer.movementY, pointerLocked: document.pointerLockElement === this._canvas });
        }

        // Fire pistol: only left mouse (button 0) should trigger the weapon.
        if (this._pistol && this._pistol.active && document.pointerLockElement) {
            if (this._mouseDown && this._mouseButton === 0) this._pistol.pullTrigger(); else this._pistol.releaseTrigger();
        }

        // Mining Tool: Left click to mine, Right click to build
        if (this._activeId === 'mining' && this._miningTool && document.pointerLockElement) {
            if (this._mouseDown && this._mouseButton === 0) this._miningTool.startMining(); else this._miningTool.stopMining();

            const rightJustReleased = (!this._mouseDown && this._prevMouseDown && this._prevMouseButton === 2);
            if (rightJustReleased) {
                this._miningTool.build();
            }
        }

        // Clear movement buffer each frame
        this._movementBuffer.movementX = 0;
        this._movementBuffer.movementY = 0;

        // Store previous mouse state for edge detection next frame
        this._prevMouseDown = this._mouseDown;
        this._prevMouseButton = this._mouseButton;
    }
}
